import type { components, operations } from '@multaqa/api-client'
import { api } from '@/lib/api'

export type CustomerResource = components['schemas']['CustomerResource']
export type CustomerFinancialSummary = components['schemas']['CustomerFinancialSummaryResource']
export type ContactResource = components['schemas']['ContactResource']
export type TagResource = components['schemas']['TagResource']
export type CustomerType = components['schemas']['CustomerType']
export type StoreCustomerRequest = components['schemas']['StoreCustomerRequest']
export type UpdateCustomerRequest = components['schemas']['UpdateCustomerRequest']
export type StoreContactRequest = components['schemas']['StoreContactRequest']
type CustomerPayloadFields = Omit<
  StoreCustomerRequest | UpdateCustomerRequest,
  'address' | 'code'
> & {
  is_walk_in?: boolean
  mobile?: string | null
  till_note?: string | null
  address?: { en?: string; ar?: string } | null
}
export type CustomerCreatePayload = CustomerPayloadFields
export type CustomerUpdatePayload = CustomerPayloadFields & { code?: string }

export type CustomerIndexParams = operations['customer.index']['parameters']['query'] & {
  /**
   * `search`, `page` and `tag_id` are read by the controller but not declared
   * in its OpenAPI operation — the index filters straight off the Request
   * instead of a validated array, so Scramble cannot see them. Declared here
   * so the list can send them; see plans/58 for making the contract itself
   * honest.
   */
  search?: string
  page?: number
  tag_id?: string
  per_page?: number
}

export async function fetchCustomers(params?: CustomerIndexParams) {
  const response = await api.GET('/directory/customers', {
    params: {
      query: params,
    },
  })
  return response.data
}

export async function fetchCustomer(id: string) {
  const response = await api.GET('/directory/customers/{customer}', {
    params: {
      path: { customer: id },
    },
  })
  return response.data?.data
}

export async function fetchCustomerFinancialSummary(id: string) {
  const response = await api.GET('/directory/customers/{customer}/summary', {
    params: { path: { customer: id } },
  })
  if (!response.data) throw new Error('Customer financial summary returned no body')
  return response.data.data
}

export async function createCustomer(data: CustomerCreatePayload) {
  const response = await api.POST('/directory/customers', {
    body: data as StoreCustomerRequest,
  })
  return response.data?.data
}

export async function updateCustomer(id: string, data: CustomerUpdatePayload) {
  const response = await api.PUT('/directory/customers/{customer}', {
    params: {
      path: { customer: id },
    },
    body: data as UpdateCustomerRequest,
  })
  return response.data?.data
}

export async function deactivateCustomer(id: string) {
  const response = await api.POST('/directory/customers/{customer}/deactivate', {
    params: {
      path: { customer: id },
    },
  })
  return response.data?.data
}

export async function addCustomerContact(customerId: string, data: StoreContactRequest) {
  const response = await api.POST('/directory/customers/{customer}/contacts', {
    params: {
      path: { customer: customerId },
    },
    body: data,
  })
  return response.data?.data
}

export async function removeCustomerContact(customerId: string, contactId: string) {
  await api.DELETE('/directory/customers/{customer}/contacts/{contact}', {
    params: {
      path: { customer: customerId, contact: contactId },
    },
  })
}

export async function attachCustomerTag(customerId: string, tagId: string) {
  const response = await api.POST('/directory/customers/{customer}/tags/{tag}', {
    params: {
      path: { customer: customerId, tag: tagId },
    },
  })
  return response.data?.data
}

export async function detachCustomerTag(customerId: string, tagId: string) {
  await api.DELETE('/directory/customers/{customer}/tags/{tag}', {
    params: {
      path: { customer: customerId, tag: tagId },
    },
  })
}

export async function fetchTags() {
  const response = await api.GET('/directory/tags', {})
  return response.data?.data
}
